import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Loader2, Trash2, Send, MapPin, Users2, EyeOff, Eye, Wrench, X, Crown, MessageCircle, Pencil, Check, MessageSquarePlus, FileText, IndianRupee, ExternalLink, Gift, ChevronDown, ChevronUp, Phone, Clock, RefreshCw, GripVertical, Search } from "lucide-react";
import MultiSelectPills from "@/components/MultiSelectPills";
import CrewMemberPicker from "@/components/CrewMemberPicker";
import ShareTrackerActions from "@/components/ShareTrackerActions";

/**
 * Shoot Crew Management — 4th tab inside /team.
 *
 *  UI structure (matches the master-sheet mockup):
 *    ┌─────────────────────────────────────────────────────────────────┐
 *    │ 🎬 Mayuri & Chaitanya · 📍 Grand Hyatt Pune  [📢 Share Schedule] │
 *    ├───────────┬────────┬───────────┬────────┬────────────┬─────────┤
 *    │ 2026-02-14│ Haldi  │ CINEMATO..│ Venu ▾ │ [Sony a7s3│ notes…  │
 *    │           │        │           │        │  · Rs4]   │         │
 *    │ 2026-02-15│ Wedding│ PHOTOGRA..│ Ayush ▾│ [Sony a7m4│ notes…  │
 *    └───────────┴────────┴───────────┴────────┴────────────┴─────────┘
 *
 *  Rows are grouped by (client_name · place) into a card. Client/Place are
 *  edited in the group header (one edit updates every child row). Job Role,
 *  Member, and Equipment are per-row dropdowns. Coordinator has full write
 *  access — the whole component is Financial-Blindness safe (no rates, no
 *  totals, no payment info).
 */
export default function ShootCrew({ canWrite, isAdmin, editClient = "", editPlace = "", openDeliverables = false }) {
  const [rows, setRows] = useState([]);
  const [staff, setStaff] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [jobRoles, setJobRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(null);
  // iter21 additions
  const [groupMeta, setGroupMeta] = useState({}); // key `${client}||${place}` → {shoot_lead_staff_id, is_archived}
  const [showArchived, setShowArchived] = useState(false);
  const [equipModal, setEquipModal] = useState(false);
  const [teamModal, setTeamModal] = useState(false);
  const [rolesModal, setRolesModal] = useState(false);
  // iter95 — Client phone lookup keyed by lowercased client_name so the group
  // header card can render `📞 +91…` right next to the couple name without
  // an extra per-card fetch.
  const [clientPhoneByName, setClientPhoneByName] = useState({});
  // iter121 — Search + collapse state for the Shoot Crew card view.
  //   * `search` filters clusters by couple / venue / lead / date.
  //   * `expandedGroups` is a Set of group.key that are currently expanded.
  //     Default: EMPTY (all cards collapsed), matching Rakesh's "start
  //     collapsed" spec. Global toolbar toggle flips every currently-visible
  //     group at once.
  const [search, setSearch] = useState("");
  const [expandedGroups, setExpandedGroups] = useState(() => new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: r }, { data: s }, { data: e }, { data: gm }] = await Promise.all([
        api.get(`/team/crew?include_archived=${showArchived}`),
        api.get("/team/staff"),
        api.get("/team/equipment"),
        api.get("/team/crew/groups"),
      ]);
      setRows(r);
      setStaff(s);
      setEquipment(e.equipment || []);
      setJobRoles(e.job_roles || []);
      const map = {};
      gm.forEach((g) => { map[`${g.client_name}||${g.place}`] = g; });
      setGroupMeta(map);
      // iter95 — fetch client phone once from Bookings so the header card can
      // display "📞 +91…" without a per-card round-trip. Non-blocking; a
      // 403/404 (coordinator without read access) just leaves the map empty.
      try {
        const { data: bookings } = await api.get("/bookings");
        const phones = {};
        (bookings || []).forEach((b) => {
          const nm = (b.client_name || "").trim().toLowerCase();
          const phone = (b.contact || "").trim();
          if (nm && phone && !phones[nm]) phones[nm] = phone;
        });
        setClientPhoneByName(phones);
      } catch { /* silently fall through — feature is additive */ }
    } finally { setLoading(false); }
  }, [showArchived]);

  useEffect(() => { load(); }, [load]);

  // Group by (client_name, place). Preserve insertion order of first-seen groups.
  const groups = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => {
      // iter46 — Global chronological rendering: one card PER (client, place,
      // event_date) instead of collapsing all dates for a client. This lets
      // sorting be strictly day-first across every couple, and lets the
      // "Share Schedule" WhatsApp payload isolate a single day.
      const key = `${r.client_name}||${r.place}||${r.event_date || ""}`;
      if (!map.has(key)) {
        map.set(key, {
          client_name: r.client_name,
          // iter113 — Carry couple_name through the grouping so the card
          // header renders "Rakesh & Preeti" instead of just "Rakesh".
          couple_name: r.couple_name || r.client_name,
          place: r.place,
          event_date: r.event_date || "",
          rows: [],
        });
      }
      map.get(key).rows.push(r);
    });
    // Within each single-day group, sort rows by position for stable ordering.
    for (const g of map.values()) {
      g.rows.sort((a, b) => (a.position || 0) - (b.position || 0));
    }
    // iter46 — Strict ascending chronological sort across ALL couples/days.
    // Groups without a date fall to the bottom so open-ended items are never
    // wedged between concrete future dates.
    const list = Array.from(map.entries()).map(([key, g]) => ({
      key,
      ...g,
      _earliest: g.event_date || "9999-99-99",
    }));
    list.sort((a, b) => {
      if (a._earliest !== b._earliest) return a._earliest.localeCompare(b._earliest);
      // Tie-break: same date — sort by couple name for deterministic ordering.
      return (a.client_name || "").localeCompare(b.client_name || "");
    });
    return list;
  }, [rows]);

  // iter102 — Resolve the target group.key when deep-linked from the Public
  // Client Tracker's admin "✎ Edit" pencil. We match by (client_name, place)
  // and pick the earliest event_date since studio-level meta (deliverables)
  // is shared across every date-card for the couple.
  const targetGroupKey = useMemo(() => {
    if (!editClient || !openDeliverables) return "";
    const wantClient = editClient.trim().toLowerCase();
    const wantPlace = (editPlace || "").trim().toLowerCase();
    const match = groups.find(
      (g) =>
        (g.client_name || "").trim().toLowerCase() === wantClient &&
        (g.place || "").trim().toLowerCase() === wantPlace,
    );
    return match?.key || "";
  }, [groups, editClient, editPlace, openDeliverables]);

  // Once the target group renders, scroll it into view. Runs a single tick
  // after groups paint so the DOM node exists.
  useEffect(() => {
    if (!targetGroupKey || loading) return;
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-testid="crew-group-${CSS.escape(targetGroupKey)}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 120);
    return () => clearTimeout(t);
  }, [targetGroupKey, loading]);

  /**
   * iter49 — Smart Continuous Event Grouping.
   *
   * Cluster same-couple date-groups whose dates lie within a 2-day continuous
   * window (i.e. gap ≤ 2 days from one date to the next in sorted order).
   * A "cluster" represents one uninterrupted wedding block for a couple —
   * e.g. Haldi (Nov 12) → Sangeet (Nov 13) → Wedding (Nov 14). Disjointed
   * events for the SAME couple (e.g. Tilak on Oct 5 vs Wedding on Nov 12)
   * become SEPARATE clusters and slot into the global chronological order
   * independently, so single-day events never get visually orphaned.
   *
   * Algorithm:
   *   1. Group date-cards by (client_name, place).
   *   2. For each couple, sort by date and greedily chain adjacent cards
   *      whose day-gap ≤ 2 into one cluster; else start a fresh cluster.
   *   3. Undated cards become their own singleton clusters (sorted last).
   *   4. Globally sort ALL clusters by earliest_date ascending.
   *
   * Rendering (below) uses `cluster.groups.length > 1` to decide whether to
   * draw a shared visual boundary + banner header around the child cards.
   */
  const CLUSTER_GAP_DAYS = 2;
  const clusters = useMemo(() => {
    const parseYMD = (s) => {
      if (!s || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
      const [y, m, d] = s.split("-").map(Number);
      return Date.UTC(y, m - 1, d);   // ms since epoch, UTC-safe (no TZ drift)
    };
    const daysGap = (a, b) => Math.abs((a - b) / 86_400_000);

    // 1. Bucket date-groups by (couple, place).
    const byCouple = new Map();
    groups.forEach((g) => {
      const ck = `${g.client_name}||${g.place}`;
      if (!byCouple.has(ck)) byCouple.set(ck, []);
      byCouple.get(ck).push(g);
    });

    const all = [];
    byCouple.forEach((coupleGroups, coupleKey) => {
      // Split dated vs undated — undated become their own singleton clusters.
      const dated = coupleGroups
        .filter((g) => g.event_date)
        .sort((a, b) => a.event_date.localeCompare(b.event_date));
      const undated = coupleGroups.filter((g) => !g.event_date);

      let current = null;
      dated.forEach((g) => {
        const d = parseYMD(g.event_date);
        if (current) {
          const lastDate = parseYMD(current.groups.at(-1).event_date);
          if (d !== null && lastDate !== null && daysGap(d, lastDate) <= CLUSTER_GAP_DAYS) {
            current.groups.push(g);
            current.latest_date = g.event_date;
            return;
          }
        }
        current = {
          cluster_id: `${coupleKey}||${g.event_date}`,
          client_name: g.client_name,
          place: g.place,
          earliest_date: g.event_date,
          latest_date: g.event_date,
          groups: [g],
        };
        all.push(current);
      });

      undated.forEach((g) => {
        all.push({
          cluster_id: `${coupleKey}||nd-${g.key}`,
          client_name: g.client_name,
          place: g.place,
          earliest_date: "",
          latest_date: "",
          groups: [g],
        });
      });
    });

    // 2. Global chronological sort of clusters. Undated fall to the bottom.
    const sortKey = (c) => c.earliest_date || "9999-99-99";
    all.sort((a, b) => {
      const ka = sortKey(a), kb = sortKey(b);
      if (ka !== kb) return ka.localeCompare(kb);
      return (a.client_name || "").localeCompare(b.client_name || "");
    });
    return all;
  }, [groups]);

  // iter121 — Filtered clusters view. Matches couple_name / client_name /
  // place / event_date (formatted or raw) / shoot lead name against a
  // lowercased search token. Multi-day clusters survive as long as ANY of
  // their date-cards match; other date-cards are still rendered inside the
  // cluster (Rakesh usually wants context around a matching date).
  const filteredClusters = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return clusters;
    const staffNameById = new Map((staff || []).map((s) => [s.id, (s.name || "").toLowerCase()]));
    return clusters.filter((c) => {
      const metaKey = `${c.client_name}||${c.place}`;
      const meta = groupMeta[metaKey] || {};
      const leadName = staffNameById.get(meta.shoot_lead_staff_id || "") || "";
      const dateBlob = c.groups
        .map((g) => (g.event_date || "").toLowerCase())
        .join(" ");
      const humanDates = c.groups
        .map((g) => {
          if (!g.event_date) return "";
          const [y, m, d] = g.event_date.split("-").map(Number);
          try {
            return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-IN", {
              day: "numeric", month: "short", year: "numeric", timeZone: "UTC",
            }).toLowerCase();
          } catch { return ""; }
        })
        .join(" ");
      const haystack = [
        c.client_name || "",
        c.place || "",
        // iter113 — also check couple_name at the group level
        c.groups.map((g) => g.couple_name || "").join(" "),
        leadName,
        dateBlob,
        humanDates,
      ].join(" ").toLowerCase();
      return haystack.includes(q);
    });
  }, [clusters, search, groupMeta, staff]);

  // iter121 — Collapse / expand helpers.
  const toggleGroup = useCallback((key) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);
  const visibleGroupKeys = useMemo(
    () => filteredClusters.flatMap((c) => c.groups.map((g) => g.key)),
    [filteredClusters],
  );
  const allVisibleExpanded =
    visibleGroupKeys.length > 0 && visibleGroupKeys.every((k) => expandedGroups.has(k));
  const toggleAllVisible = useCallback(() => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (allVisibleExpanded) {
        // Collapse only the currently visible ones; preserve any others.
        visibleGroupKeys.forEach((k) => next.delete(k));
      } else {
        visibleGroupKeys.forEach((k) => next.add(k));
      }
      return next;
    });
  }, [allVisibleExpanded, visibleGroupKeys]);

  // iter112 — Admin-triggered bulk resync of booking.deliverables → every
  // Shoot Crew card's meta. Convenient for propagating an existing bulk
  // update of Booking modals without redeploying, and for filling any
  // legacy groups that never had a source booking on file.
  const [resyncing, setResyncing] = useState(false);
  const handleResyncDeliverables = useCallback(async () => {
    setResyncing(true);
    try {
      const { data } = await api.post("/team/crew/resync-deliverables");
      toast.success(`Deliverables backfill: ${data.synced} synced / ${data.scanned} scanned`);
      await load();
    } catch (e) {
      toast.error("Resync failed — " + (e?.response?.data?.detail || e?.message || "unknown error"));
    } finally {
      setResyncing(false);
    }
  }, [load]);

  /**
   * Map of `event_date` → { staffIds: Set, equipment: Set } aggregating every
   * row on that date across ALL groups. The `CrewRow` uses this to hide
   * members / equipment already booked elsewhere on the same day — the
   * same-day double-booking would 409 on save anyway, so we filter them out
   * up-front. Own-row selections are always kept visible (see filtering
   * logic below in CrewRow).
   */
  const bookedByDate = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => {
      const d = r.event_date || "";
      if (!d) return;
      if (!map.has(d)) map.set(d, { staffIds: new Set(), equipment: new Set() });
      const bucket = map.get(d);
      if (r.staff_id) bucket.staffIds.add(r.staff_id);
      (r.equipment || []).forEach((e) => bucket.equipment.add(e));
    });
    return map;
  }, [rows]);

  // -------------------- mutations --------------------
  const showConflict = (err) => {
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail;
    if (status === 409 && typeof detail === "string") {
      toast.error(detail);   // exact message: "Staff Conflict: …" / "Equipment Conflict: …"
      return true;
    }
    return false;
  };

  const patchRow = useCallback(async (id, patch) => {
    if (!canWrite) return;
    // Optimistically update, then rollback + toast if the backend rejects on
    // a same-day double-booking (409). We keep the row's original values so
    // the offending field visibly clears / snaps back to the prior state.
    const before = rows.find((r) => r.id === id);
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    try {
      const { data } = await api.patch(`/team/crew/${id}`, patch);
      setRows((prev) => prev.map((r) => (r.id === id ? data : r)));
    } catch (err) {
      // Roll back the field so the UI shows the previous value again.
      if (before) setRows((prev) => prev.map((r) => (r.id === id ? before : r)));
      if (!showConflict(err)) load();
    }
  }, [canWrite, load, rows]);

  const addRow = async (group) => {
    if (!canWrite) return;
    try {
      const { data } = await api.post("/team/crew", {
        client_name: group.client_name,
        place: group.place,
        event_date: group.rows.at(-1)?.event_date || "",
        event: group.rows.at(-1)?.event || "",
        job_role: "",
        staff_id: "",
        equipment: [],
        expo: "",
      });
      setRows((prev) => [...prev, data]);
    } catch (err) {
      if (!showConflict(err)) toast.error("Could not add row");
    }
  };

  const addGroup = async () => {
    if (!canWrite) return;
    const { data } = await api.post("/team/crew", {
      client_name: "New client",
      place: "",
      event_date: "",
      event: "",
      job_role: "",
      staff_id: "",
      equipment: [],
      expo: "",
    });
    setRows((prev) => [...prev, data]);
  };

  const removeRow = async (id) => {
    if (!canWrite) return;
    setRows((prev) => prev.filter((r) => r.id !== id));
    try {
      await api.delete(`/team/crew/${id}`);
    } catch { load(); }
  };

  // iter118 — Drag-and-drop row reorder within a Shoot Crew group.
  // The group prop carries the exact ordered rows; we reorder locally
  // (optimistic), persist via POST /team/crew/reorder, and roll back on
  // any error. `position` is updated on both the row and the local state
  // so the UI stays consistent even before the server round-trip returns.
  const reorderRows = useCallback(async (group, fromId, toId) => {
    if (!canWrite || fromId === toId) return;
    const groupIds = group.rows.map((r) => r.id);
    const fromIdx = groupIds.indexOf(fromId);
    const toIdx = groupIds.indexOf(toId);
    if (fromIdx === -1 || toIdx === -1) return;
    const nextOrder = [...groupIds];
    nextOrder.splice(fromIdx, 1);
    nextOrder.splice(toIdx, 0, fromId);
    // Optimistic local swap: patch `position` on each affected row.
    setRows((prev) => {
      const posMap = new Map(nextOrder.map((rid, i) => [rid, i]));
      return prev.map((r) => (posMap.has(r.id) ? { ...r, position: posMap.get(r.id) } : r));
    });
    try {
      await api.post("/team/crew/reorder", { ordered_ids: nextOrder });
    } catch (err) {
      toast.error("Could not save new order — " + (err?.response?.data?.detail || err?.message || "unknown error"));
      load();
    }
  }, [canWrite, load]);

  /** Upsert group-level metadata (shoot lead + archive flag). */
  const patchGroupMeta = async (group, patch) => {
    if (!canWrite) return;
    try {
      const { data } = await api.post("/team/crew/groups", {
        client_name: group.client_name,
        place: group.place,
        ...patch,
      });
      setGroupMeta((prev) => ({ ...prev, [`${group.client_name}||${group.place}`]: data }));
      if (patch.is_archived === true) {
        toast.success(`Archived “${group.client_name}”. Toggle "Show hidden" to restore.`);
        if (!showArchived) load();  // remove from view
      } else if (patch.is_archived === false) {
        toast.success(`Restored “${group.client_name}”`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save group settings");
    }
  };

  /** Rename every row in a group when the group header edits Client Name or Place. */
  const patchGroupHeader = async (group, patch) => {
    if (!canWrite) return;
    // optimistic local rename
    setRows((prev) => prev.map((r) => (r.client_name === group.client_name && r.place === group.place ? { ...r, ...patch } : r)));
    await Promise.all(group.rows.map((r) => api.patch(`/team/crew/${r.id}`, patch).catch(() => null)));
  };

  const shareOnWhatsApp = async (group) => {
    setSending(group.key);
    try {
      // iter46 — dispatch only THIS day's operational metrics (event type,
      // location, crew/lead). Prior versions sent the entire client's
      // package which mixed past + future dates.
      const { data } = await api.post("/team/crew/notify", {
        client_name: group.client_name,
        place: group.place,
        event_date: group.event_date || undefined,
      });
      if (data.wa_me_url) {
        window.open(data.wa_me_url, "_blank", "noopener,noreferrer");
        // iter95 — confirm which extras were injected so the coordinator
        // sees at a glance that the client phone + shoot call time made it
        // into the payload.
        const extras = [];
        if (data.call_time) extras.push(`⏰ ${data.call_time}`);
        if (data.client_phone) extras.push(`📞 +${String(data.client_phone).replace(/^\+/, "")}`);
        if (data.shoot_lead?.whatsapp) extras.push(`👑 ${data.shoot_lead.name || "Lead"}`);
        const suffix = extras.length ? ` · ${extras.join(" · ")}` : "";
        toast.success(`Schedule for ${group.client_name} opened in WhatsApp (${data.row_count} rows${suffix})`);
      } else {
        toast.error("Could not build WhatsApp link");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to build schedule");
    } finally { setSending(null); }
  };

  if (loading) {
    return (
      <div className="card p-8 text-center text-stone-500 text-sm" data-testid="crew-loading">
        <Loader2 className="w-4 h-4 mr-2 inline animate-spin" /> Loading crew board…
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="crew-tab">
      {/* iter121 — Search + Expand/Collapse toolbar. Search filters clusters
           by couple / venue / lead / date. Expand-All flips every currently-
           visible card at once. */}
      <div className="card p-3 flex flex-wrap items-center gap-2" data-testid="crew-search-toolbar">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" strokeWidth={2} />
          <input
            type="text"
            className="input !pl-10 py-2 text-sm"
            placeholder="Search couple, venue, lead, or date..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="crew-search-input"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-rose-700"
              title="Clear search"
              data-testid="crew-search-clear"
            >
              <X className="w-4 h-4" strokeWidth={2} />
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={toggleAllVisible}
          className="btn btn-outline btn-sm inline-flex items-center gap-1.5"
          disabled={visibleGroupKeys.length === 0}
          title={allVisibleExpanded ? "Collapse every visible card" : "Expand every visible card"}
          data-testid="crew-toggle-all"
        >
          {allVisibleExpanded ? <ChevronUp className="w-3.5 h-3.5" strokeWidth={2} /> : <ChevronDown className="w-3.5 h-3.5" strokeWidth={2} />}
          {allVisibleExpanded ? "Collapse All" : "Expand All"}
        </button>
        <div className="text-[11px] text-stone-500 font-mono" data-testid="crew-results-count">
          {search ? `${filteredClusters.length} match${filteredClusters.length === 1 ? "" : "es"}` : `${clusters.length} shoot${clusters.length === 1 ? "" : "s"}`}
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-[11px] text-stone-500 max-w-xl">
          Group crew by client &amp; venue. Each row is one assignment. Use <span className="font-semibold">📢 Share Schedule</span> to open a pre-filled WhatsApp message routed to the Shoot Lead.
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="inline-flex items-center gap-2 text-xs text-stone-600 cursor-pointer" data-testid="show-archived-toggle">
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} className="accent-rose-700" />
            Show hidden / archived events
          </label>
          {canWrite && (
            <>
              <button type="button" onClick={() => setEquipModal(true)} className="btn btn-outline btn-sm" data-testid="open-equipment-modal">
                <Wrench className="w-3.5 h-3.5" strokeWidth={1.8} /> Manage Equipment Inventory
              </button>
              <button type="button" onClick={() => setTeamModal(true)} className="btn btn-outline btn-sm" data-testid="open-team-modal">
                <Users2 className="w-3.5 h-3.5" strokeWidth={1.8} /> Manage Team Members
              </button>
              {isAdmin && (
                <button type="button" onClick={() => setRolesModal(true)} className="btn btn-outline btn-sm" data-testid="open-roles-modal">
                  <Pencil className="w-3.5 h-3.5" strokeWidth={1.8} /> Manage Job Roles
                </button>
              )}
              {/* iter112 — One-click bulk resync so admins can propagate every
                   booking's DELIVERABLES (INVOICE PDF) list to every couple's
                   Shoot Crew card. Respects `deliverables_source = "manual"`. */}
              {isAdmin && (
                <button
                  type="button"
                  onClick={handleResyncDeliverables}
                  disabled={resyncing}
                  className="btn btn-outline btn-sm"
                  title="Bulk backfill: mirror every booking's DELIVERABLES list into its Shoot Crew card. Manual overrides are respected."
                  data-testid="resync-deliverables-btn"
                >
                  {resyncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.8} />}
                  Resync Deliverables
                </button>
              )}
              <button type="button" onClick={addGroup} className="btn btn-primary btn-sm" data-testid="crew-add-group">
                <Plus className="w-3.5 h-3.5" /> Add client
              </button>
            </>
          )}
        </div>
      </div>

      {filteredClusters.length === 0 && (
        <div className="card p-8 text-center text-stone-400 text-sm">
          {search
            ? <>No shoots match <span className="font-mono text-stone-600">&ldquo;{search}&rdquo;</span>. Try a different couple, venue, or date.</>
            : showArchived
            ? "No archived clients to show."
            : <>No active crew rows yet. Click <span className="font-semibold text-stone-600">Add client</span> to start scheduling.</>}
        </div>
      )}

      {filteredClusters.map((cluster) => {
        const isMultiDay = cluster.groups.length > 1;
        // Human-readable date range for the cluster banner header.
        // iter49: Nov 12 → Nov 14 · 3 days · Client Name
        const fmtBanner = (ymd) => {
          if (!ymd) return "";
          const [y, m, d] = ymd.split("-").map(Number);
          const dt = new Date(Date.UTC(y, m - 1, d));
          return dt.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
        };
        return (
          <div
            key={cluster.cluster_id}
            className={isMultiDay ? "rounded-2xl border-2 border-rose-200 bg-rose-50/40 p-3 space-y-3 shadow-sm" : "space-y-3"}
            data-testid={`crew-cluster-${cluster.cluster_id}`}
            data-cluster-size={cluster.groups.length}
          >
            {isMultiDay && (
              <div className="flex flex-wrap items-center justify-between gap-2 px-2 pt-1 pb-2 border-b border-rose-200/70">
                <div className="flex items-center gap-2">
                  <span className="text-lg">🎊</span>
                  <div>
                    <div className="font-heading text-sm font-semibold text-rose-900">
                      {cluster.client_name || "Untitled couple"}
                      {cluster.place ? <span className="text-stone-500 font-normal"> · {cluster.place}</span> : null}
                    </div>
                    <div className="text-[11px] text-stone-500 uppercase tracking-wider">
                      Continuous wedding block · {fmtBanner(cluster.earliest_date)} → {fmtBanner(cluster.latest_date)}
                    </div>
                  </div>
                </div>
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-rose-800 bg-rose-100 border border-rose-200 rounded-full px-2 py-0.5">
                  {cluster.groups.length} consecutive days
                </span>
              </div>
            )}
            {cluster.groups.map((group) => {
              // iter50 fix: `groupMeta` is keyed by (client, place) — NOT
              // (client, place, date) — because studio-level metadata (shoot
              // lead, archive state, package value, deliverables, terms) is
              // shared across every date-card for a couple. iter46 changed
              // `group.key` to include event_date so each date renders as its
              // own card; this made `groupMeta[group.key]` always return
              // undefined → Shoot Lead dropdown reverted to "— None —" after
              // every write. Derive the correct 2-token metaKey here.
              const metaKey = `${group.client_name}||${group.place}`;
              const meta = groupMeta[metaKey] || {};
              const archived = !!meta.is_archived;
              // iter121 — Card collapse state. Default is collapsed (empty
              // Set); user expands per-card or via the toolbar Expand-All.
              const isExpanded = expandedGroups.has(group.key);
              return (
                <div key={group.key} className={`card overflow-hidden ${archived ? "opacity-70 border-dashed" : ""}`} data-testid={`crew-group-${group.key}`}>
                  <GroupHeader
                    group={group}
                    meta={meta}
                    staff={staff}
                    canWrite={canWrite}
                    isAdmin={isAdmin}
                    sending={sending === group.key}
                    clientPhone={clientPhoneByName[(group.client_name || "").trim().toLowerCase()] || ""}
                    onShare={() => shareOnWhatsApp(group)}
                    onHeaderChange={(patch) => patchGroupHeader(group, patch)}
                    onMetaChange={(patch) => patchGroupMeta(group, patch)}
                    expanded={isExpanded}
                    onToggleExpanded={() => toggleGroup(group.key)}
                    memberCount={group.rows.length}
                  />

                  {/* iter121 — Collapsed cards hide the crew table + admin
                       extras block. Header stays interactive so Rakesh can
                       glance couple + venue + lead without scrolling. */}
                  {isExpanded && (
                  <>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-xs">
                      <thead className="bg-stone-50 border-y border-stone-200">
                        <tr className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">
                          {canWrite && <th className="w-6" />}
                          <th className="px-3 py-2 text-left w-28">Date</th>
                          <th className="px-3 py-2 text-left w-28">Event</th>
                          <th className="px-3 py-2 text-left w-40">Job role</th>
                          <th className="px-3 py-2 text-left w-40">Member</th>
                          <th className="px-3 py-2 text-left">Equipment</th>
                          <th className="px-3 py-2 text-left w-48">Expo / notes</th>
                          {canWrite && <th className="w-8" />}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-stone-100">
                        {group.rows.map((r) => (
                          <CrewRow
                            key={r.id}
                            row={r}
                            group={group}
                            staff={staff}
                            equipment={equipment}
                            jobRoles={jobRoles}
                            canWrite={canWrite}
                            onPatch={patchRow}
                            onRemove={removeRow}
                            onReorder={reorderRows}
                            bookedForDate={bookedByDate.get(r.event_date || "") || null}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {canWrite && (
                    <div className="border-t border-stone-100 px-3 py-2 bg-stone-50/60">
                      <button type="button" onClick={() => addRow(group)}
                        className="text-xs text-stone-500 hover:text-rose-700 inline-flex items-center gap-1"
                        data-testid={`crew-add-row-${group.key}`}>
                        <Plus className="w-3 h-3" strokeWidth={2.2} /> Add crew row
                      </button>
                    </div>
                  )}

                  {/* iter122 — Moodboard editor. Writable by every writer
                       role (admin + manager + coordinator) since moodboard
                       content is not financial. Shows a compact URL textarea
                       + notes; auto-saves on blur through onMetaChange. */}
                  {canWrite && !archived && (
                    <MoodboardEditor
                      groupKey={group.key}
                      urls={meta.moodboard_urls || ""}
                      notes={meta.moodboard_notes || ""}
                      onChange={(patch) => patchGroupMeta(group, patch)}
                    />
                  )}

                  {/* Iter 27+29 — Contract Generator + Deliverables + Payments (admin only) */}
                  {isAdmin && (
                    <GroupExtras
                      group={group}
                      canWrite={canWrite}
                      archived={archived}
                      staff={staff}
                      meta={meta}
                      onMetaChange={(patch) => patchGroupMeta(group, patch)}
                      autoOpenDeliverables={group.key === targetGroupKey}
                    />
                  )}
                  </>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}

      {equipModal && (
        <EquipmentInventoryModal onClose={() => setEquipModal(false)} onChanged={() => api.get("/team/equipment").then(({ data }) => setEquipment(data.equipment || []))} />
      )}
      {teamModal && (
        <ManageTeamModal onClose={() => setTeamModal(false)} onChanged={() => api.get("/team/staff").then(({ data }) => setStaff(data))} />
      )}
      {rolesModal && (
        <ManageRolesModal
          onClose={() => setRolesModal(false)}
          onChanged={() => api.get("/team/equipment").then(({ data }) => setJobRoles(data.job_roles || []))}
        />
      )}
    </div>
  );
}

// ==================================================================
// Group header — merged Client Name + Place cells + Shoot Lead + actions.
// ==================================================================
function GroupHeader({ group, meta, staff, canWrite, isAdmin, sending, clientPhone, onShare, onHeaderChange, onMetaChange, expanded = true, onToggleExpanded, memberCount = 0 }) {
  // iter113 — Prefer couple_name in the header. Falls back to client_name.
  // The couple_name lives in a separate DB field, so editing here doesn't
  // touch the `shoot_crew.client_name` join key (which stays as the
  // booking's primary point of contact). Rakesh can rename the couple
  // freely (e.g., "Rakesh & Preeti") without breaking any downstream join.
  const initialCouple = group.couple_name || group.client_name || "";
  const [client, setClient] = useState(initialCouple);
  const [place, setPlace] = useState(group.place);
  // iter200 — Per-day call time. This card represents ONE (client, place,
  // event_date) triple, but `meta` is the shared group doc — so read the
  // day-scoped entry first, fall back to the legacy group-level value.
  const dayKey = group.event_date || "";
  const initialCallTime = (dayKey && meta?.call_times?.[dayKey]) || meta?.call_time || "";
  const [callTime, setCallTime] = useState(initialCallTime);
  useEffect(() => { setClient(group.couple_name || group.client_name || ""); }, [group.couple_name, group.client_name]);
  useEffect(() => { setPlace(group.place); }, [group.place]);
  useEffect(() => {
    setCallTime((dayKey && meta?.call_times?.[dayKey]) || meta?.call_time || "");
  }, [meta?.call_times, meta?.call_time, dayKey]);

  const leadId = meta?.shoot_lead_staff_id || "";
  const leadStaff = staff.find((s) => s.id === leadId);
  const archived = !!meta?.is_archived;

  // Distinct staff assigned to any row in this group. `assignedMembers` is
  // the full list (drives button enabled state), `membersWithWA` is the
  // subset that actually has a valid WhatsApp number for the deep-link.
  const assignedMembers = useMemo(() => {
    const staffIds = [...new Set(group.rows.map((r) => r.staff_id).filter(Boolean))];
    return staffIds.map((id) => staff.find((s) => s.id === id)).filter(Boolean);
  }, [group.rows, staff]);
  const membersWithWA = useMemo(
    () => assignedMembers.filter((s) => s.whatsapp && String(s.whatsapp).replace(/\D/g, "").length >= 10),
    [assignedMembers],
  );
  const hasMembers = assignedMembers.length > 0;

  const createGroupLink = () => {
    if (!hasMembers) return;
    const clientLabel = (group.client_name || "Event").toUpperCase();
    const groupTitle = `Kahani · ${clientLabel}`;

    if (membersWithWA.length === 0) {
      toast.error(
        `${assignedMembers.length} member${assignedMembers.length > 1 ? "s" : ""} assigned but none have WhatsApp numbers. Add numbers in Manage Team Members first.`,
      );
      return;
    }

    const lines = [
      `*${groupTitle}*`,
      ``,
      `Please create a new WhatsApp group with the following crew:`,
      ``,
      ...assignedMembers.map((m, i) => {
        const num = String(m.whatsapp || "").replace(/\D/g, "");
        return num
          ? `${i + 1}. ${m.name} — +${num}`
          : `${i + 1}. ${m.name} — (no WhatsApp on file)`;
      }),
      ``,
      `_Suggested group name:_ *${groupTitle}*`,
      `_Total members:_ ${assignedMembers.length}` + (membersWithWA.length < assignedMembers.length ? ` (${membersWithWA.length} with WhatsApp)` : ""),
    ];
    const text = lines.join("\n");
    try { navigator.clipboard?.writeText?.(text)?.catch?.(() => {}); } catch (err) { if (process.env.NODE_ENV !== "production") console.debug("clipboard noop:", err); }

    // Prefer the first assigned WhatsApp number as the deep-link target so
    // WhatsApp opens directly on a chat where the message is pre-filled —
    // one tap to forward into a new group, exactly as requested.
    const primary = membersWithWA[0];
    const primaryDigits = String(primary.whatsapp).replace(/\D/g, "");
    const url = `https://wa.me/${primaryDigits}?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank", "noopener,noreferrer");
    toast.success(
      `Group draft for "${groupTitle}" copied · ${assignedMembers.length} members. Opening WhatsApp with ${primary.name} — hit "New Group" and paste.`,
    );
  };

  return (
    <div className="px-4 py-3 bg-gradient-to-r from-rose-50 to-white border-b border-stone-200 flex items-start gap-3 flex-wrap">
      {/* iter121 — Chevron toggle for card collapse. Sits ahead of the couple
           icon so Rakesh can tap the same corner to open/close every card. */}
      {onToggleExpanded && (
        <button
          type="button"
          onClick={onToggleExpanded}
          className="w-8 h-8 rounded-lg border border-stone-200 bg-white flex items-center justify-center text-stone-500 hover:text-rose-800 hover:border-rose-200 hover:bg-rose-50 transition shrink-0"
          title={expanded ? "Collapse card" : "Expand card"}
          data-testid={`crew-group-toggle-${group.key}`}
          aria-expanded={expanded}
        >
          {expanded ? <ChevronUp className="w-4 h-4" strokeWidth={2.2} /> : <ChevronDown className="w-4 h-4" strokeWidth={2.2} />}
        </button>
      )}
      <div className="flex items-center gap-2 flex-1 min-w-[260px]">
        <div className="w-8 h-8 rounded-lg bg-rose-100 border border-rose-200 flex items-center justify-center text-rose-800 shrink-0">
          <Users2 className="w-4 h-4" strokeWidth={1.8} />
        </div>
        <div className="flex-1 min-w-0">
          {/* iter121 — Compact summary chip appears when the card is collapsed.
               Shows date + shoot lead + assigned-member count without needing
               to open the full crew table. */}
          {!expanded && (
            <div className="flex flex-wrap items-center gap-2 mt-1" data-testid={`crew-group-summary-${group.key}`}>
              {group.event_date && (
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-stone-600 bg-stone-100 border border-stone-200 rounded-full px-2 py-0.5 font-mono">
                  {group.event_date}
                </span>
              )}
              {leadStaff && (
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                  <Crown className="w-2.5 h-2.5" strokeWidth={2.2} /> {leadStaff.name}
                </span>
              )}
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
                <Users2 className="w-2.5 h-2.5" strokeWidth={2.2} /> {memberCount} crew
              </span>
            </div>
          )}
          <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold" data-testid="crew-group-couple-label">
            Couple Name
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <input
              className="font-heading text-lg font-semibold text-stone-900 bg-transparent border-0 focus:outline-none focus:ring-1 focus:ring-rose-300 rounded px-1 min-w-[180px]"
              value={client}
              disabled={!canWrite || archived}
              onChange={(e) => setClient(e.target.value)}
              onBlur={() => client !== (group.couple_name || group.client_name) && onHeaderChange({ couple_name: client })}
              placeholder="Couple Name (e.g. ARTI & RUTUJ)"
              data-testid="crew-group-client-input"
            />
            {archived && (
              <span className="badge badge-neutral text-[10px] uppercase tracking-wider bg-stone-200 text-stone-600 border-stone-300">Archived</span>
            )}
          </div>
          <div className="flex items-center gap-1 text-xs text-stone-500 mt-0.5">
            <MapPin className="w-3 h-3" strokeWidth={2} />
            <input
              className="flex-1 bg-transparent border-0 focus:outline-none focus:ring-1 focus:ring-rose-300 rounded px-1 min-w-[140px]"
              value={place}
              disabled={!canWrite || archived}
              onChange={(e) => setPlace(e.target.value)}
              onBlur={() => place !== group.place && onHeaderChange({ place })}
              placeholder="Venue / place"
              data-testid="crew-group-place-input"
            />
          </div>
          {/* iter95 — Client phone (from Bookings) — chip render, tap-to-call */}
          {clientPhone && (
            <a
              href={`tel:+${String(clientPhone).replace(/\D/g, "")}`}
              className="inline-flex items-center gap-1 text-[10px] font-mono text-rose-800 bg-rose-50 border border-rose-200 rounded-full px-2 py-0.5 mt-1 hover:bg-rose-100 transition"
              data-testid={`crew-client-phone-${group.key}`}
              title="Tap to call the client"
            >
              <Phone className="w-3 h-3" strokeWidth={2.2} /> Client: +{String(clientPhone).replace(/^\+/, "")}
            </a>
          )}
        </div>
      </div>

      {/* Shoot Lead + actions */}
      {/* iter103 — Overflow fix: previously this block was `shrink-0` which
           kept its intrinsic width intact and let the Archive/Hide button
           get clipped by the card's `overflow-hidden` at ≤1500px widths.
           Switching to `flex-1 min-w-0 justify-end` lets the block claim
           remaining row width, drop below the couple-name column (via the
           outer flex-wrap), and wrap its own buttons onto multiple lines. */}
      <div className="flex items-center gap-2 flex-wrap flex-1 min-w-0 justify-end">
        {/* iter95 — Shoot Call Time input. Free-form so studios can type
             "07:00 AM" / "Sharp 6 AM" without validation friction. Persisted
             on blur; included in the Share Schedule WhatsApp payload.
             iter200 — Scoped PER DAY: writes carry `event_date` so each
             day-card keeps its own independent time (stored in
             `call_times.<date>` on the group doc; legacy `call_time`
             remains the fallback for days without their own entry). */}
        <label className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-stone-600" data-testid={`crew-call-time-label-${group.key}`}>
          <Clock className="w-3.5 h-3.5 text-emerald-700" strokeWidth={2} />
          Shoot Call Time
          <input
            type="text"
            className="input py-1 text-xs w-[110px]"
            value={callTime}
            disabled={!canWrite || archived}
            placeholder="e.g. 07:00 AM"
            onChange={(e) => setCallTime(e.target.value)}
            onBlur={() => {
              const trimmed = callTime.trim();
              const saved = (dayKey && meta?.call_times?.[dayKey]) || meta?.call_time || "";
              if (trimmed !== saved) {
                onMetaChange({ call_time: trimmed, event_date: dayKey || undefined });
              }
            }}
            data-testid={`crew-call-time-input-${group.key}`}
          />
        </label>
        {isAdmin && (
          <PackageValueInput
            groupKey={group.key}
            value={meta?.total_package_value}
            disabled={!canWrite || archived}
            onChange={(v) => onMetaChange({ total_package_value: v })}
          />
        )}
        <label className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-stone-600" data-testid={`crew-lead-picker-${group.key}`}>
          <Crown className="w-3.5 h-3.5 text-amber-600" strokeWidth={2} />
          Shoot Lead
          <div className="min-w-[180px]">
            <CrewMemberPicker
              staff={staff}
              value={leadId}
              disabled={!canWrite || archived}
              onChange={(v) => onMetaChange({ shoot_lead_staff_id: v })}
              placeholder="— None —"
              recentsKey="shoot-lead"
              testid={`crew-lead-select-${group.key}`}
            />
          </div>
        </label>
        {leadStaff?.whatsapp && (
          <span className="inline-flex items-center gap-1 text-[10px] font-mono text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5" data-testid={`crew-lead-whatsapp-${group.key}`}>
            <MessageCircle className="w-3 h-3" strokeWidth={2.2} /> +{leadStaff.whatsapp}
          </span>
        )}
        {/* iter122 — Moodboard quick-view pill for assigned crew. Shows a
             single "View Moodboard" link when the group has any URLs on file.
             Opens the first URL in a new tab; the tracker + booking modal
             show the full list. */}
        {meta?.moodboard_urls && (
          (() => {
            const firstMoodUrl = String(meta.moodboard_urls || "").split(/\r?\n/).map((s) => s.trim()).find(Boolean);
            const count = String(meta.moodboard_urls || "").split(/\r?\n/).map((s) => s.trim()).filter(Boolean).length;
            if (!firstMoodUrl) return null;
            return (
              <a
                href={firstMoodUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[10px] font-semibold text-fuchsia-800 bg-fuchsia-50 border border-fuchsia-200 rounded-full px-2 py-0.5 hover:bg-fuchsia-100"
                title={`View Moodboard (${count} link${count === 1 ? "" : "s"})`}
                data-testid={`crew-moodboard-view-${group.key}`}
              >
                🎨 View Moodboard
                {count > 1 && <span className="opacity-75">({count})</span>}
              </a>
            );
          })()
        )}
        <button
          type="button"
          onClick={onShare}
          disabled={sending || archived}
          className="btn btn-primary btn-sm shrink-0"
          data-testid="crew-share-btn"
        >
          {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" strokeWidth={2} />}
          📢 Share Schedule
        </button>
        {/* iter98 — Share the public client tracker on WhatsApp / copy the link.
             iter106 — Admin-only. Coordinator / staff roles never see these
             two buttons; only Rakesh (admin) can share the tracker link. */}
        {isAdmin && (
          <ShareTrackerActions
            clientName={group.client_name}
            place={group.place}
            whatsapp={clientPhone}
            size="sm"
          />
        )}
        <button
          type="button"
          onClick={createGroupLink}
          disabled={!hasMembers || archived}
          title={
            hasMembers
              ? `Compose a WhatsApp group draft for ${assignedMembers.length} member${assignedMembers.length > 1 ? "s" : ""}`
              : "Assign at least one crew member to enable this"
          }
          className="btn btn-outline btn-sm shrink-0 border-emerald-300 text-emerald-800 hover:bg-emerald-50 disabled:opacity-40"
          data-testid={`crew-create-group-btn-${group.key}`}
        >
          <MessageSquarePlus className="w-3.5 h-3.5" strokeWidth={2} />
          <span>💬 Create Group Link</span>
          {hasMembers && (
            <span className="ml-1 text-[10px] font-mono opacity-75" data-testid={`crew-create-group-count-${group.key}`}>
              ({assignedMembers.length})
            </span>
          )}
        </button>
        {canWrite && (
          <button
            type="button"
            onClick={() => onMetaChange({ is_archived: !archived })}
            className="btn btn-outline btn-sm shrink-0"
            title={archived ? "Restore this event" : "Hide/archive this event"}
            data-testid={`crew-hide-btn-${group.key}`}
          >
            {archived
              ? <><Eye className="w-3.5 h-3.5" strokeWidth={1.8} /> Restore</>
              : <><EyeOff className="w-3.5 h-3.5" strokeWidth={1.8} /> Hide event</>}
          </button>
        )}
      </div>
    </div>
  );
}

// ==================================================================
// Crew row — per-row dropdowns for job role, member, equipment.
// ==================================================================
function CrewRow({ row, group, staff, equipment, jobRoles, canWrite, onPatch, onRemove, onReorder, bookedForDate }) {
  // iter118 — Drag-drop state. When the row is being dragged we render a
  // subtle rose ring; when a *different* row hovers over us, we render
  // an emerald top-border so Rakesh can see the drop target.
  const [dragOver, setDragOver] = useState(false);
  const onDragStart = (e) => {
    e.dataTransfer.setData("text/plain", row.id);
    e.dataTransfer.effectAllowed = "move";
  };
  const onDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (!dragOver) setDragOver(true);
  };
  const onDragLeave = () => setDragOver(false);
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const fromId = e.dataTransfer.getData("text/plain");
    if (fromId && fromId !== row.id && onReorder && group) {
      onReorder(group, fromId, row.id);
    }
  };

  // Hide staff already booked on this row's date by ANOTHER row. Always
  // keep the currently-selected staff member visible (own selection).
  const availableStaff = useMemo(() => {
    if (!bookedForDate) return staff;
    return staff.filter((s) => s.id === row.staff_id || !bookedForDate.staffIds.has(s.id));
  }, [staff, bookedForDate, row.staff_id]);

  // Hide equipment items already picked on any other same-date row. Items
  // present in THIS row's selection stay visible so the user can un-check.
  const availableEquipment = useMemo(() => {
    if (!bookedForDate) return equipment;
    const own = new Set(row.equipment || []);
    return equipment
      .map((group) => ({
        category: group.category,
        items: group.items.filter((it) => own.has(it) || !bookedForDate.equipment.has(it)),
      }))
      .filter((group) => group.items.length > 0);
  }, [equipment, bookedForDate, row.equipment]);

  return (
    <tr
      data-testid={`crew-row-${row.id}`}
      onDragOver={canWrite ? onDragOver : undefined}
      onDragLeave={canWrite ? onDragLeave : undefined}
      onDrop={canWrite ? onDrop : undefined}
      className={dragOver ? "outline outline-2 outline-emerald-400 -outline-offset-2" : ""}
    >
      {canWrite && (
        <td className="align-top py-2 pl-2 pr-0">
          <div
            draggable
            onDragStart={onDragStart}
            className="cursor-grab active:cursor-grabbing text-stone-400 hover:text-rose-700 inline-flex items-center justify-center w-5 h-5 rounded hover:bg-stone-100 select-none"
            title="Drag to reorder"
            data-testid={`crew-row-grip-${row.id}`}
          >
            <GripVertical className="w-3.5 h-3.5" strokeWidth={2} />
          </div>
        </td>
      )}
      <td className="px-3 py-2 align-top">
        <input type="date"
          className="input py-1 text-xs w-full"
          value={row.event_date || ""}
          disabled={!canWrite}
          onChange={(e) => onPatch(row.id, { event_date: e.target.value })}
          data-testid={`crew-row-date-${row.id}`}
        />
      </td>
      <td className="px-3 py-2 align-top">
        <input type="text"
          className="input py-1 text-xs w-full"
          value={row.event || ""}
          disabled={!canWrite}
          onChange={(e) => onPatch(row.id, { event: e.target.value })}
          placeholder="Haldi / Wedding…"
          data-testid={`crew-row-event-${row.id}`}
        />
      </td>
      <td className="px-3 py-2 align-top">
        <select
          className="input py-1 text-xs w-full font-semibold"
          value={row.job_role || ""}
          disabled={!canWrite}
          onChange={(e) => onPatch(row.id, { job_role: e.target.value })}
          data-testid={`crew-row-role-${row.id}`}
        >
          <option value="">— Role —</option>
          {jobRoles.map((role) => <option key={role} value={role}>{role}</option>)}
        </select>
      </td>
      <td className="px-3 py-2 align-top">
        <CrewMemberPicker
          staff={availableStaff}
          value={row.staff_id || ""}
          disabled={!canWrite}
          onChange={(v) => onPatch(row.id, { staff_id: v })}
          placeholder="— Member —"
          recentsKey={`crew-row-${row.job_role || "any"}`}
          testid={`crew-row-member-${row.id}`}
        />
      </td>
      <td className="px-3 py-2 align-top min-w-[220px]">
        <MultiSelectPills
          value={row.equipment || []}
          onChange={(next) => onPatch(row.id, { equipment: next })}
          options={availableEquipment}
          disabled={!canWrite}
          placeholder="Add gear…"
          testid={`crew-row-equip-${row.id}`}
        />
      </td>
      <td className="px-3 py-2 align-top">
        <input type="text"
          className="input py-1 text-xs w-full"
          value={row.expo || ""}
          disabled={!canWrite}
          onChange={(e) => onPatch(row.id, { expo: e.target.value })}
          placeholder="Notes / expo"
          data-testid={`crew-row-expo-${row.id}`}
        />
      </td>
      {canWrite && (
        <td className="px-2 py-2 align-top">
          <button type="button"
            onClick={() => onRemove(row.id)}
            className="p-1 text-stone-400 hover:text-red-700 hover:bg-red-50 rounded"
            title="Delete row"
            data-testid={`crew-row-delete-${row.id}`}
          >
            <Trash2 className="w-3.5 h-3.5" strokeWidth={2} />
          </button>
        </td>
      )}
    </tr>
  );
}

// ==================================================================
// Manage Equipment Inventory — add / delete gear across categories.
// ==================================================================
function EquipmentInventoryModal({ onClose, onChanged }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [category, setCategory] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/team/equipment/list");
      setItems(data);
      // Auto-fill the category select with an existing one so admins don't
      // accidentally create a rogue category via a typo.
      if (data.length && !category) setCategory(data[0].category);
    } finally { setLoading(false); }
  }, [category]);
  useEffect(() => { load(); }, [load]);

  const categories = useMemo(
    () => Array.from(new Set(items.map((i) => i.category))).sort(),
    [items],
  );
  const grouped = useMemo(() => {
    const g = {};
    items.forEach((i) => { (g[i.category] ??= []).push(i); });
    return g;
  }, [items]);

  const add = async () => {
    if (!name.trim() || !category.trim()) return;
    setBusy(true);
    try {
      await api.post("/team/equipment", { name: name.trim(), category: category.trim() });
      setName("");
      toast.success(`Added ${name.trim()} to ${category.trim()}`);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add");
    } finally { setBusy(false); }
  };

  const remove = async (id, itemName) => {
    if (!window.confirm(`Remove "${itemName}" from inventory?`)) return;
    try {
      await api.delete(`/team/equipment/${id}`);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/45 flex items-center justify-center px-4" onClick={onClose}>
      <div className="bg-white w-full max-w-2xl rounded-xl shadow-2xl max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="equipment-modal">
        <div className="px-5 h-14 flex items-center justify-between border-b border-stone-200">
          <div className="flex items-center gap-2">
            <Wrench className="w-4 h-4 text-rose-700" strokeWidth={1.8} />
            <h3 className="font-heading font-semibold">Manage Equipment Inventory</h3>
          </div>
          <button onClick={onClose} className="btn-ghost p-2 rounded"><X className="w-5 h-5" /></button>
        </div>

        <div className="px-5 py-3 border-b border-stone-100 grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2 items-end">
          <div>
            <label className="label">Item name</label>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Sony a7c ii" data-testid="equip-add-name" />
          </div>
          <div>
            <label className="label">Category</label>
            <input list="equip-cats" className="input" value={category} onChange={(e) => setCategory(e.target.value)} placeholder="Cameras" data-testid="equip-add-category" />
            <datalist id="equip-cats">
              {categories.map((c) => <option key={c} value={c} />)}
            </datalist>
          </div>
          <button type="button" onClick={add} disabled={busy || !name.trim() || !category.trim()} className="btn btn-primary" data-testid="equip-add-submit">
            <Plus className="w-3.5 h-3.5" /> Add
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="equip-list">
          {loading ? (
            <div className="text-center text-sm text-stone-500"><Loader2 className="w-4 h-4 mr-2 inline animate-spin" /> Loading…</div>
          ) : (
            categories.map((c) => (
              <div key={c}>
                <div className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold mb-2">{c}</div>
                <div className="flex flex-wrap gap-2">
                  {grouped[c].map((item) => (
                    <span key={item.id} className="inline-flex items-center gap-1 bg-stone-100 border border-stone-200 rounded-full pl-3 pr-0.5 py-1 text-xs" data-testid={`equip-item-${item.id}`}>
                      {item.name}
                      <button onClick={() => remove(item.id, item.name)} className="p-1 rounded-full hover:bg-red-100 hover:text-red-700" aria-label="Delete">
                        <X className="w-3 h-3" strokeWidth={2.4} />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

// ==================================================================
// Manage Team Members — add active staff with rate + WhatsApp number.
// (Add is admin-only; coord sees a read-only list — Financial Blindness.)
// ==================================================================
function ManageTeamModal({ onClose, onChanged }) {
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [rate, setRate] = useState("");
  const [wa, setWa] = useState("");
  const [busy, setBusy] = useState(false);
  // Detect admin by probing an admin-gated field on staff (per_day_rate present).
  const canAddStaff = useMemo(() => staffList.some((s) => "per_day_rate" in s), [staffList]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/team/staff?include_inactive=true");
      setStaffList(data);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!name.trim() || !rate) return;
    setBusy(true);
    try {
      await api.post("/team/staff", {
        name: name.trim(),
        per_day_rate: Number(rate),
        whatsapp: wa.trim(),
      });
      setName(""); setRate(""); setWa("");
      toast.success(`Added ${name.trim()} to the team`);
      await load();
      onChanged?.();
    } catch (e) {
      const msg = e.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Could not add member");
    } finally { setBusy(false); }
  };

  const patchWhatsapp = async (id, value) => {
    try {
      await api.patch(`/team/staff/${id}`, { whatsapp: value.trim() });
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update");
    }
  };

  const patchStaff = async (id, patch) => {
    try {
      await api.patch(`/team/staff/${id}`, patch);
      await load();
      onChanged?.();
      toast.success("Saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    }
  };

  const deleteStaff = async (staff) => {
    if (!canAddStaff) return;  // admin-only guard
    const ok = window.confirm(
      `Permanently remove ${staff.name} from the team?\n\n` +
      `This cannot be undone. Historical crew rows must be deleted first, ` +
      `otherwise the removal will be blocked.`,
    );
    if (!ok) return;
    try {
      await api.delete(`/team/staff/${staff.id}?hard=true`);
      toast.success(`Removed ${staff.name}`);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/45 flex items-center justify-center px-4" onClick={onClose}>
      <div className="bg-white w-full max-w-4xl rounded-xl shadow-2xl max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="team-modal">
        <div className="px-5 h-14 flex items-center justify-between border-b border-stone-200">
          <div className="flex items-center gap-2">
            <Users2 className="w-4 h-4 text-rose-700" strokeWidth={1.8} />
            <h3 className="font-heading font-semibold">Manage Team Members</h3>
          </div>
          <button onClick={onClose} className="btn-ghost p-2 rounded"><X className="w-5 h-5" /></button>
        </div>

        {canAddStaff ? (
          <div className="px-5 py-3 border-b border-stone-100 grid grid-cols-1 sm:grid-cols-[1fr_120px_150px_auto] gap-2 items-end">
            <div>
              <label className="label">Full name</label>
              <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Nikhil" data-testid="team-add-name" />
            </div>
            <div>
              <label className="label">Day rate (₹)</label>
              <input type="number" min="0" className="input" value={rate} onChange={(e) => setRate(e.target.value)} placeholder="3000" data-testid="team-add-rate" />
            </div>
            <div>
              <label className="label">WhatsApp #</label>
              <input className="input" value={wa} onChange={(e) => setWa(e.target.value)} placeholder="91XXXXXXXXXX" data-testid="team-add-whatsapp" />
            </div>
            <button type="button" onClick={add} disabled={busy || !name.trim() || !rate} className="btn btn-primary" data-testid="team-add-submit">
              <Plus className="w-3.5 h-3.5" /> Add
            </button>
          </div>
        ) : (
          <div className="px-5 py-3 border-b border-stone-100 text-xs text-stone-500 italic">
            Ask an admin to add new team members. You can edit WhatsApp numbers below.
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-5 space-y-2" data-testid="team-list">
          {loading ? (
            <div className="text-center text-sm text-stone-500"><Loader2 className="w-4 h-4 mr-2 inline animate-spin" /> Loading…</div>
          ) : (
            staffList.map((s) => (
              <TeamMemberRow
                key={s.id}
                staff={s}
                canAdmin={canAddStaff}
                onPatchWhatsapp={(v) => patchWhatsapp(s.id, v)}
                onPatchStaff={(patch) => patchStaff(s.id, patch)}
                onDelete={() => deleteStaff(s)}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}

// ---- one team member row with inline Edit + Delete (admin only) ----------
function TeamMemberRow({ staff, canAdmin, onPatchWhatsapp, onPatchStaff, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(staff.name || "");
  const [rate, setRate] = useState(String(staff.per_day_rate ?? ""));

  useEffect(() => { setName(staff.name || ""); setRate(String(staff.per_day_rate ?? "")); }, [staff.name, staff.per_day_rate]);

  const save = async () => {
    const patch = {};
    if (name.trim() && name.trim() !== staff.name) patch.name = name.trim();
    if (rate !== "" && Number(rate) !== Number(staff.per_day_rate)) patch.per_day_rate = Number(rate);
    if (Object.keys(patch).length === 0) { setEditing(false); return; }
    await onPatchStaff(patch);
    setEditing(false);
  };

  const cancel = () => {
    setName(staff.name || "");
    setRate(String(staff.per_day_rate ?? ""));
    setEditing(false);
  };

  return (
    <div className={`flex items-center gap-3 border border-stone-200 rounded-md px-3 py-2 ${staff.active ? "" : "opacity-60"}`} data-testid={`team-member-${staff.id}`}>
      <div className="flex-1 min-w-0">
        {editing ? (
          <div className="flex items-center gap-2">
            <input
              className="input py-1 text-sm flex-1 min-w-[140px]"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Full name"
              data-testid={`team-edit-name-${staff.id}`}
            />
            <div className="inline-flex items-center gap-1">
              <span className="text-stone-400 text-xs">₹</span>
              <input
                type="number"
                min="0"
                className="input py-1 text-sm w-24 text-right font-mono"
                value={rate}
                onChange={(e) => setRate(e.target.value)}
                placeholder="3000"
                data-testid={`team-edit-rate-${staff.id}`}
              />
              <span className="text-[10px] text-stone-400">/day</span>
            </div>
          </div>
        ) : (
          <>
            <div className="font-semibold text-stone-800 truncate">
              {staff.name} {!staff.active && <span className="badge badge-neutral text-[10px]">Inactive</span>}
            </div>
            {canAdmin && (
              <div className="text-[11px] text-stone-500 font-mono">
                ₹{Number(staff.per_day_rate || 0).toLocaleString("en-IN")} / day
              </div>
            )}
          </>
        )}
      </div>

      <label className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold shrink-0">WhatsApp</label>
      <WhatsappInput staff={staff} onSave={onPatchWhatsapp} />

      {canAdmin && (
        <div className="inline-flex items-center gap-1">
          {editing ? (
            <>
              <button
                type="button"
                onClick={save}
                className="p-1.5 rounded hover:bg-emerald-100 text-emerald-700"
                title="Save"
                data-testid={`team-edit-save-${staff.id}`}
              >
                <Check className="w-4 h-4" strokeWidth={2.4} />
              </button>
              <button
                type="button"
                onClick={cancel}
                className="p-1.5 rounded hover:bg-stone-100 text-stone-500"
                title="Cancel"
                data-testid={`team-edit-cancel-${staff.id}`}
              >
                <X className="w-4 h-4" strokeWidth={2.4} />
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="p-1.5 rounded hover:bg-rose-50 text-stone-400 hover:text-rose-700"
                title="Edit name & rate"
                data-testid={`team-edit-${staff.id}`}
              >
                <Pencil className="w-4 h-4" strokeWidth={2} />
              </button>
              <button
                type="button"
                onClick={onDelete}
                className="p-1.5 rounded hover:bg-red-50 text-stone-400 hover:text-red-700"
                title="Permanently remove"
                data-testid={`team-delete-${staff.id}`}
              >
                <Trash2 className="w-4 h-4" strokeWidth={2} />
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function WhatsappInput({ staff, onSave }) {
  const [v, setV] = useState(staff.whatsapp || "");
  useEffect(() => { setV(staff.whatsapp || ""); }, [staff.whatsapp]);
  // Wrap in a fixed-width shrink-proof container — the `.input` class in
  // index.css applies `w-full` which would otherwise steal all the flex
  // space and squash the name/rate column next to it.
  return (
    <div className="w-40 shrink-0">
      <input
        className="input py-1 text-xs font-mono"
        value={v}
        onChange={(e) => setV(e.target.value)}
        onBlur={() => v !== (staff.whatsapp || "") && onSave(v)}
        placeholder="91XXXXXXXXXX"
        data-testid={`team-member-whatsapp-${staff.id}`}
      />
    </div>
  );
}



// ==================================================================
// Iter 27 — GroupExtras: Contract Generator + Payment Milestones
// ==================================================================
function GroupExtras({ group, canWrite, archived, staff, meta, onMetaChange, autoOpenDeliverables = false }) {
  const [contracts, setContracts] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gen, setGen] = useState(false);
  const [initPay, setInitPay] = useState(false);
  const [settings, setSettings] = useState({});

  const backendPublicUrl = process.env.REACT_APP_BACKEND_URL;
  // iter29 — re-fetch milestones whenever the admin edits total_package_value.
  const totalPkg = meta?.total_package_value ?? null;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = `?client_name=${encodeURIComponent(group.client_name)}&place=${encodeURIComponent(group.place || "")}`;
      const [c, p, s] = await Promise.all([
        api.get(`/team/contracts${q}`),
        api.get(`/team/payments${q}`),
        api.get(`/team/studio-settings`),
      ]);
      setContracts(c.data || []);
      setMilestones(p.data || []);
      setSettings(s.data || {});
    } catch (err) { console.debug("keeping last-known state:", err); }
    finally { setLoading(false); }
  }, [group.client_name, group.place]);
  useEffect(() => { load(); }, [load]);
  // Re-pull payments when the parent's total_package_value changes so the
  // 30/50/20 cards reflect the new amounts backend recomputed for us.
  useEffect(() => {
    if (totalPkg === null) return;
    const q = `?client_name=${encodeURIComponent(group.client_name)}&place=${encodeURIComponent(group.place || "")}`;
    api.get(`/team/payments${q}`).then(({ data }) => setMilestones(data || [])).catch(() => {});
  }, [totalPkg, group.client_name, group.place]);

  const latestContract = contracts[0];  // sorted DESC by created_at
  const isSigned = latestContract?.status === "signed";

  const generateContract = async () => {
    if (!canWrite || gen) return;
    setGen(true);
    try {
      const { data } = await api.post("/team/contracts", {
        client_name: group.client_name,
        place: group.place || "",
      });
      const url = `${window.location.origin}/contracts/${data.token}`;
      // Copy the link to clipboard so admin can share it easily.
      try { navigator.clipboard?.writeText?.(url)?.catch?.(() => {}); } catch (err) { if (process.env.NODE_ENV !== "production") console.debug("clipboard noop:", err); }
      window.open(url, "_blank", "noopener,noreferrer");
      toast.success(`Contract link ready — copied to clipboard for ${group.client_name}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not generate contract");
    } finally { setGen(false); }
  };

  const initPayments = async () => {
    if (!canWrite || initPay) return;
    setInitPay(true);
    try {
      const { data } = await api.post("/team/payments/init", {
        client_name: group.client_name,
        place: group.place || "",
      });
      setMilestones(data);
      toast.success("Payment schedule initialised");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not initialise payments");
    } finally { setInitPay(false); }
  };

  const patchMilestone = async (m, patch) => {
    if (!canWrite) return;
    setMilestones((prev) => prev.map((x) => (x.id === m.id ? { ...x, ...patch } : x)));
    try {
      const { data } = await api.patch(`/team/payments/${m.id}`, patch);
      setMilestones((prev) => prev.map((x) => (x.id === m.id ? data : x)));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update milestone");
      load();
    }
  };

  const sendReminder = (m) => {
    const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
    const studioName = settings.studio_name || "Kahani by Rakesh Janardan";
    const bank = settings.bank_details || "Bank details on file";
    const upi = settings.upi_id ? `\nUPI: *${settings.upi_id}*` : "";
    const text = [
      `Hi ${group.client_name},`,
      ``,
      `Hope you're doing well! A gentle reminder for the *${m.label}* milestone for your upcoming event:`,
      ``,
      `• Amount due: *${inr(m.amount)}*`,
      `• Due date: *${m.due_date}*`,
      ``,
      `Payment options:`,
      bank,
      upi,
      ``,
      `Please share a screenshot once done and we'll acknowledge. Thanks so much!`,
      ``,
      `— ${studioName}`,
    ].filter(Boolean).join("\n");
    try { navigator.clipboard?.writeText?.(text)?.catch?.(() => {}); } catch (err) { if (process.env.NODE_ENV !== "production") console.debug("clipboard noop:", err); }
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank", "noopener,noreferrer");
    toast.success("Reminder copied — opening WhatsApp");
  };

  const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

  return (
    <div className="border-t border-stone-200 px-4 py-3 bg-gradient-to-b from-stone-50/60 to-white space-y-3" data-testid={`crew-group-extras-${group.key}`}>
      {/* iter29 — Deliverables + Terms & Conditions (admin editable) */}
      <ContractContentEditor
        groupKey={group.key}
        deliverables={meta?.deliverables || ""}
        deliverablesInherited={!!meta?.deliverables_inherited}
        termsOverride={meta?.terms_override || ""}
        disabled={!canWrite || archived}
        onSave={onMetaChange}
        autoOpen={autoOpenDeliverables}
      />

      {/* Contract row */}
      <div className="flex items-center flex-wrap gap-2">
        <div className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-stone-500">
          <FileText className="w-3.5 h-3.5 text-rose-700" strokeWidth={2} />
          Contract
        </div>
        {loading ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin text-stone-400" />
        ) : isSigned ? (
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-800 bg-emerald-100 border border-emerald-300 rounded-full px-2.5 py-0.5" data-testid={`crew-contract-signed-${group.key}`}>
            <Check className="w-3 h-3" strokeWidth={3} /> Signed ✅ by {latestContract.signed_by_name}
          </span>
        ) : latestContract ? (
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-0.5" data-testid={`crew-contract-pending-${group.key}`}>
            Awaiting signature
          </span>
        ) : (
          <span className="text-[11px] text-stone-400 italic">No contract yet</span>
        )}
        {latestContract && (
          <a
            href={`${window.location.origin}/contracts/${latestContract.token}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[11px] text-rose-700 hover:text-rose-900 inline-flex items-center gap-0.5 underline"
            data-testid={`crew-contract-link-${group.key}`}
          >
            Open link <ExternalLink className="w-3 h-3" />
          </a>
        )}
        {canWrite && !archived && (
          <button
            type="button"
            onClick={generateContract}
            disabled={gen || isSigned}
            className="ml-auto btn btn-outline btn-sm shrink-0 border-rose-300 text-rose-800 hover:bg-rose-50 disabled:opacity-40"
            data-testid={`crew-generate-contract-btn-${group.key}`}
            title={isSigned ? "Contract already signed — regenerate is disabled" : "Compile a fresh contract and open a signing link"}
          >
            {gen ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" strokeWidth={2} />}
            {isSigned ? "Contract locked" : latestContract ? "Regenerate Contract" : "📄 Generate Contract"}
          </button>
        )}
      </div>

      {/* Payment schedule */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <div className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-stone-500">
            <IndianRupee className="w-3.5 h-3.5 text-emerald-700" strokeWidth={2} />
            Payment Schedule
          </div>
          {milestones.length === 0 && canWrite && !archived && (
            <button
              type="button"
              onClick={initPayments}
              disabled={initPay}
              className="ml-auto btn btn-outline btn-sm shrink-0 border-emerald-300 text-emerald-800 hover:bg-emerald-50 disabled:opacity-40"
              data-testid={`crew-init-payments-btn-${group.key}`}
            >
              {initPay ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" strokeWidth={2} />}
              Initialise 30 / 50 / 20 split
            </button>
          )}
        </div>
        {milestones.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid={`crew-payment-schedule-${group.key}`}>
            {milestones.map((m) => (
              <MilestoneCard
                key={m.id}
                m={m}
                canWrite={canWrite && !archived}
                onPatch={(patch) => patchMilestone(m, patch)}
                onRemind={() => sendReminder(m)}
                inr={inr}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function MilestoneCard({ m, canWrite, onPatch, onRemind, inr }) {
  const received = m.status === "received";
  const overdue = !received && m.due_date && m.due_date < new Date().toISOString().slice(0, 10);
  return (
    <div
      className={`border rounded-lg p-3 space-y-1.5 transition-colors ${
        received
          ? "bg-emerald-50 border-emerald-300"
          : overdue
            ? "bg-red-50 border-red-300"
            : "bg-white border-stone-200"
      }`}
      data-testid={`crew-milestone-${m.milestone_type}-${m.id}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] uppercase tracking-wider font-semibold text-stone-500">
          {m.label} <span className="text-stone-400">({Math.round(m.percent * 100)}%)</span>
        </div>
        <button
          type="button"
          onClick={() => canWrite && onPatch({ status: received ? "pending" : "received" })}
          disabled={!canWrite}
          className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full border transition-colors ${
            received
              ? "bg-emerald-500 text-white border-emerald-500"
              : overdue
                ? "bg-red-100 text-red-800 border-red-300 hover:bg-red-200"
                : "bg-stone-100 text-stone-700 border-stone-300 hover:bg-stone-200"
          } ${!canWrite ? "cursor-default" : ""}`}
          data-testid={`crew-milestone-toggle-${m.id}`}
        >
          {received ? "✓ Received" : overdue ? "Overdue" : "Pending"}
        </button>
      </div>
      <div className="flex items-baseline gap-2">
        <span className="font-heading text-lg font-bold text-emerald-800 tabular-nums" data-testid={`crew-milestone-amount-${m.id}`}>
          {inr(m.amount)}
        </span>
        <span className="text-[11px] text-stone-500">due {m.due_date}</span>
      </div>
      {!received && canWrite && (
        <button
          type="button"
          onClick={onRemind}
          className="w-full text-[11px] font-semibold text-emerald-800 border border-emerald-300 rounded px-2 py-1 hover:bg-emerald-100 inline-flex items-center justify-center gap-1"
          data-testid={`crew-milestone-remind-${m.id}`}
        >
          <MessageCircle className="w-3 h-3" strokeWidth={2.2} /> 📲 Send Payment Reminder
        </button>
      )}
    </div>
  );
}


// ==================================================================
// Iter 28 — Total Package Value input (admin only). Empty = use fallback
// role-based rates (₹20k / ₹17k) when generating contracts/payments.
// ==================================================================
function PackageValueInput({ groupKey, value, disabled, onChange }) {
  const [draft, setDraft] = useState(value == null ? "" : String(value));
  useEffect(() => { setDraft(value == null ? "" : String(value)); }, [value]);

  const commit = () => {
    const t = draft.trim();
    if (t === "") {
      // Empty means "revert to fallback" — send 0 (no explicit override
      // possible in the current backend model without a null sentinel).
      // For MVP, treat 0 as clear-to-fallback OR document that admin sets
      // explicit 0 to mean "no charge". We simply skip patching if the
      // string is empty and the server value is already null.
      if (value != null) onChange(0);
      return;
    }
    const n = Number(t);
    if (Number.isNaN(n) || n < 0) {
      setDraft(value == null ? "" : String(value));
      return;
    }
    if (n !== Number(value ?? -1)) onChange(n);
  };

  return (
    <label className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-stone-600" title="Total package value (₹). Empty falls back to role-based rates.">
      <span className="text-stone-500 uppercase tracking-wider">Package ₹</span>
      <input
        type="number"
        min="0"
        step="1000"
        className="input py-1 text-xs w-28 text-right font-mono"
        placeholder="auto"
        value={draft}
        disabled={disabled}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
        data-testid={`crew-package-value-${groupKey}`}
      />
    </label>
  );
}


// ==================================================================
// Iter 29 — Deliverables + Terms & Conditions editor (admin only).
// Collapsed by default to keep the group card scannable. Commits on blur.
// ==================================================================
const DEFAULT_TERMS_FRONTEND = (
  '1. Kahani by Rakesh Janardan ("the Studio") will provide professional photography and cinematography services for the event(s) listed above.\n' +
  '2. Client agrees to make payments per the milestone schedule shared separately. Late payments may cause delivery delays.\n' +
  '3. Final edited deliverables will be shared digitally within 60 days of the last event date, subject to timely feedback and payment completion.\n' +
  '4. The Studio retains copyright over all deliverables and reserves the right to use non-personal images for portfolio and marketing purposes.\n' +
  '5. Cancellations within 30 days of the event forfeit the booking advance.\n' +
  '6. This agreement is deemed accepted upon digital signature below.'
);

function ContractContentEditor({ groupKey, deliverables, deliverablesInherited = false, termsOverride, disabled, onSave, autoOpen = false }) {
  const [open, setOpen] = useState(false);
  const [delDraft, setDelDraft] = useState(deliverables || "");
  const [termsDraft, setTermsDraft] = useState(termsOverride || DEFAULT_TERMS_FRONTEND);
  const deliverablesRef = useRef(null);
  const [flash, setFlash] = useState(false);

  useEffect(() => { setDelDraft(deliverables || ""); }, [deliverables]);
  useEffect(() => { setTermsDraft(termsOverride || DEFAULT_TERMS_FRONTEND); }, [termsOverride]);

  // iter102 — Deep-link support: when the parent flags this group as the
  // one the admin wants to edit, expand the panel and focus the textarea
  // on first mount. A brief rose ring "flash" draws the eye. We only run
  // this once per mount to avoid re-triggering on unrelated re-renders.
  useEffect(() => {
    if (!autoOpen) return;
    setOpen(true);
    const t = setTimeout(() => {
      const el = deliverablesRef.current;
      if (el) {
        el.focus();
        try { el.setSelectionRange(el.value.length, el.value.length); } catch { /* older browsers */ }
      }
      setFlash(true);
      setTimeout(() => setFlash(false), 1600);
    }, 220);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpen]);

  const commitDeliverables = () => {
    const v = delDraft.trim();
    if (v !== (deliverables || "").trim()) onSave({ deliverables: v });
  };
  const commitTerms = () => {
    const v = termsDraft.trim();
    const current = (termsOverride || "").trim();
    // Saving the default text as-is clears any override.
    const normalizedDefault = DEFAULT_TERMS_FRONTEND.trim();
    if (v === normalizedDefault && current === "") return;
    if (v !== current) onSave({ terms_override: v });
  };
  const resetTerms = () => {
    setTermsDraft(DEFAULT_TERMS_FRONTEND);
    if ((termsOverride || "").trim()) onSave({ terms_override: "" });
  };

  return (
    <div className="rounded-lg border border-stone-200 bg-white" data-testid={`crew-contract-content-${groupKey}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 hover:bg-stone-50"
        data-testid={`crew-contract-content-toggle-${groupKey}`}
      >
        <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-stone-500">
          <Gift className="w-3.5 h-3.5 text-rose-700" strokeWidth={2} />
          Deliverables & Terms
        </span>
        <span className="text-[10px] text-stone-400 italic">
          {deliverablesInherited
            ? "auto-synced from booking"
            : deliverables
              ? "custom deliverables saved"
              : "no deliverables"} · {termsOverride ? "custom terms" : "default terms"}
        </span>
        {open ? <ChevronUp className="w-3.5 h-3.5 text-stone-400" /> : <ChevronDown className="w-3.5 h-3.5 text-stone-400" />}
      </button>
      {open && (
        <div className="px-3 pb-3 pt-1 space-y-3">
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold block">
                Deliverables (renders on public contract as &ldquo;Deliverables Included&rdquo;)
              </label>
              {deliverablesInherited && (
                <span
                  className="text-[10px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 font-semibold"
                  title="These lines were auto-synced from the Booking modal's DELIVERABLES (INVOICE PDF) list. Editing here will lock a manual override that future Booking edits won't overwrite."
                  data-testid={`crew-deliverables-inherited-${groupKey}`}
                >
                  Auto-synced from Booking
                </span>
              )}
            </div>
            <textarea
              ref={deliverablesRef}
              className={`input py-2 text-xs w-full font-mono resize-none transition-shadow ${flash ? "ring-2 ring-rose-400 ring-offset-1" : ""}`}
              rows={3}
              placeholder="1 Cinematic Film&#10;2 Hardcover Albums (12&quot; × 18&quot;)&#10;Raw Data on drive"
              value={delDraft}
              disabled={disabled}
              onChange={(e) => setDelDraft(e.target.value)}
              onBlur={commitDeliverables}
              data-testid={`crew-deliverables-${groupKey}`}
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold block">
                Terms & Conditions (edit, add, or delete clauses)
              </label>
              <button
                type="button"
                onClick={resetTerms}
                disabled={disabled}
                className="text-[10px] text-stone-500 hover:text-rose-700 underline disabled:opacity-40"
                data-testid={`crew-terms-reset-${groupKey}`}
              >
                Reset to default
              </button>
            </div>
            <textarea
              className="input py-2 text-xs w-full font-mono resize-y"
              rows={7}
              value={termsDraft}
              disabled={disabled}
              onChange={(e) => setTermsDraft(e.target.value)}
              onBlur={commitTerms}
              data-testid={`crew-terms-${groupKey}`}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ==================================================================
// Iter 31 — Manage Job Roles modal (admin only).
// Add / rename / delete roles. Backend rewrites shoot_crew.job_role on
// rename so historical rows stay in sync. Delete refuses (409) when the
// role is still referenced.
// ==================================================================
function ManageRolesModal({ onClose, onChanged }) {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/team/job-roles");
      setRoles(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load roles");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    try {
      await api.post("/team/job-roles", { name });
      setNewName("");
      await load();
      onChanged?.();
      toast.success(`Added "${name}"`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add");
    } finally { setBusy(false); }
  };

  const rename = async (role, newName) => {
    const trimmed = newName.trim();
    if (!trimmed || trimmed === role.name) return;
    try {
      await api.patch(`/team/job-roles/${role.id}`, { name: trimmed });
      await load();
      onChanged?.();
      toast.success(`Renamed to "${trimmed}"`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not rename");
    }
  };

  const remove = async (role) => {
    if (!window.confirm(`Delete the role "${role.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/team/job-roles/${role.id}`);
      await load();
      onChanged?.();
      toast.success(`Removed "${role.name}"`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/45 flex items-center justify-center px-4" onClick={onClose}>
      <div className="bg-white w-full max-w-lg rounded-xl shadow-2xl max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="roles-modal">
        <div className="px-5 h-14 flex items-center justify-between border-b border-stone-200">
          <div className="flex items-center gap-2">
            <Pencil className="w-4 h-4 text-rose-700" strokeWidth={1.8} />
            <h3 className="font-heading font-semibold">Manage Job Roles</h3>
          </div>
          <button onClick={onClose} className="btn-ghost p-2 rounded" data-testid="roles-modal-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="px-5 py-3 border-b border-stone-100 flex items-end gap-2">
          <div className="flex-1">
            <label className="label">Add a new role</label>
            <input
              className="input"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") add(); }}
              placeholder="e.g. Drone Operator, Assistant, MUA"
              data-testid="roles-add-name"
            />
          </div>
          <button
            type="button"
            onClick={add}
            disabled={busy || !newName.trim()}
            className="btn btn-primary"
            data-testid="roles-add-submit"
          >
            <Plus className="w-3.5 h-3.5" /> Add
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-2" data-testid="roles-list">
          {loading ? (
            <div className="text-center text-sm text-stone-500"><Loader2 className="w-4 h-4 mr-2 inline animate-spin" /> Loading…</div>
          ) : (
            roles.map((r) => <RoleRow key={r.id} role={r} onRename={(n) => rename(r, n)} onDelete={() => remove(r)} />)
          )}
          {!loading && roles.length === 0 && (
            <div className="text-center text-xs text-stone-400 italic py-8">No roles yet — add one above.</div>
          )}
        </div>
        <div className="px-5 py-2 text-[10px] text-stone-500 border-t border-stone-100">
          Renaming updates all past crew rows. Deleting is blocked if any crew row still uses the role.
        </div>
      </div>
    </div>
  );
}

function RoleRow({ role, onRename, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(role.name);
  useEffect(() => { setDraft(role.name); }, [role.name]);

  const commit = () => {
    setEditing(false);
    if (draft.trim() && draft.trim() !== role.name) onRename(draft);
    else setDraft(role.name);
  };
  return (
    <div className="flex items-center gap-2 border border-stone-200 rounded-md px-3 py-2" data-testid={`role-row-${role.id}`}>
      {editing ? (
        <input
          autoFocus
          className="input py-1 text-sm flex-1"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") { setDraft(role.name); setEditing(false); } }}
          data-testid={`role-edit-input-${role.id}`}
        />
      ) : (
        <span className="flex-1 font-semibold text-stone-800 truncate" data-testid={`role-name-${role.id}`}>{role.name}</span>
      )}
      {editing ? (
        <button type="button" onClick={commit} className="p-1.5 rounded hover:bg-emerald-100 text-emerald-700" title="Save" data-testid={`role-save-${role.id}`}>
          <Check className="w-4 h-4" strokeWidth={2.4} />
        </button>
      ) : (
        <button type="button" onClick={() => setEditing(true)} className="p-1.5 rounded hover:bg-rose-50 text-stone-400 hover:text-rose-700" title="Rename" data-testid={`role-edit-${role.id}`}>
          <Pencil className="w-4 h-4" strokeWidth={2} />
        </button>
      )}
      <button type="button" onClick={onDelete} className="p-1.5 rounded hover:bg-red-50 text-stone-400 hover:text-red-700" title="Delete" data-testid={`role-delete-${role.id}`}>
        <Trash2 className="w-4 h-4" strokeWidth={2} />
      </button>
    </div>
  );
}


// ==================================================================
// iter122 — Moodboard & Inspiration editor for a Shoot Crew group.
// Two textareas (URLs — one per line, plus free-form notes). Auto-
// saves on blur through the `onChange({ moodboard_urls, moodboard_notes })`
// prop. Renders inside the expanded card, visible to every writer role.
// ==================================================================
function MoodboardEditor({ groupKey, urls, notes, onChange }) {
  const [localUrls, setLocalUrls] = useState(urls || "");
  const [localNotes, setLocalNotes] = useState(notes || "");
  useEffect(() => { setLocalUrls(urls || ""); }, [urls]);
  useEffect(() => { setLocalNotes(notes || ""); }, [notes]);

  const commit = (field, value) => {
    if (value === (field === "moodboard_urls" ? urls : notes)) return;
    onChange({ [field]: value });
  };
  const urlList = String(localUrls || "").split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
  return (
    <div className="border-t border-stone-100 bg-fuchsia-50/30 px-4 py-3" data-testid={`crew-moodboard-editor-${groupKey}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-widest text-fuchsia-900 font-bold inline-flex items-center gap-1.5">
          🎨 Moodboard &amp; Inspiration
          {urlList.length > 0 && (
            <span className="font-mono text-fuchsia-700 bg-white border border-fuchsia-200 rounded-full px-1.5 py-0 tracking-normal font-semibold">
              {urlList.length}
            </span>
          )}
        </div>
        {urlList[0] && (
          <a
            href={urlList[0]}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-[10px] font-semibold text-fuchsia-800 hover:text-fuchsia-950 hover:underline"
            data-testid={`crew-moodboard-open-${groupKey}`}
          >
            View first link ↗
          </a>
        )}
      </div>
      <div className="grid md:grid-cols-2 gap-2">
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-stone-500 font-semibold mb-0.5">
            Links (one per line — Pinterest, Drive, references)
          </label>
          <textarea
            className="input py-1.5 text-xs font-mono"
            rows={3}
            value={localUrls}
            onChange={(e) => setLocalUrls(e.target.value)}
            onBlur={(e) => commit("moodboard_urls", e.target.value)}
            placeholder="https://pin.it/…&#10;https://drive.google.com/…"
            data-testid={`crew-moodboard-urls-input-${groupKey}`}
          />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-stone-500 font-semibold mb-0.5">
            Notes (theme, colour, wardrobe, must-have shots)
          </label>
          <textarea
            className="input py-1.5 text-xs"
            rows={3}
            value={localNotes}
            onChange={(e) => setLocalNotes(e.target.value)}
            onBlur={(e) => commit("moodboard_notes", e.target.value)}
            placeholder="Warm sunset palette. Bride wants slow-mo veil shot at 4 pm sharp…"
            data-testid={`crew-moodboard-notes-input-${groupKey}`}
          />
        </div>
      </div>
    </div>
  );
}

